import {
  useApiRequirementsV10GroupsRetrieve,
  useBenefitConfigurationV10ChangesList,
  useRequirementsV10GroupsRequirementsList,
} from '@ben/api/gen';
import { REQUIREMENTS_PAGE_SIZE } from '@ben/columbia/api/requirements';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { asDomainTotals, groupByDomain } from './domain-section';
import type { DomainTotals } from './domain-section';
import type { RunStatus } from './status';
import { isLiveRunStatus, requirementChipCounts } from './status';
import { useGroupDetailParams } from './use-group-detail-params';
import { useStartRun } from './use-start-run';

const pollWhileLive = (status: RunStatus | undefined): number | false =>
  isLiveRunStatus(status) ? 5000 : false;

const EMPTY_STATUS_COUNTS = {
  total: 0,
  dirty: 0,
  proposed: 0,
  outOfScope: 0,
  clean: 0,
} as const;

/**
 * All data fetching, derived state, and handlers for the group detail screen.
 *
 * Extracted from the screen component so the rendering stays focused on
 * layout, and so the performance-sensitive memos (domain map, awaiting
 * changes, chip counts) are not rebuilt on every poll-driven re-render.
 */
export const useGroupDetail = () => {
  const { groupId = '' } = useParams();
  const navigate = useNavigate();
  const {
    statusFilter,
    setStatusFilter,
    openRequirementId,
    setOpenRequirementId,
  } = useGroupDetailParams();

  // A run applies asynchronously through Temporal. Polling while live lets the
  // screen settle on its own rather than needing a manual reload. The group is
  // polled too: its run status is what decides whether to keep polling.
  const groupQuery = useApiRequirementsV10GroupsRetrieve(groupId, {
    query: {
      enabled: Boolean(groupId),
      refetchInterval: (data) =>
        pollWhileLive(data?.group.latestRun?.status as RunStatus | undefined),
    },
  });

  const runStatus = groupQuery.data?.group.latestRun?.status as
    | RunStatus
    | undefined;

  // Filtering by status is a backend comparison, so it is a query param rather
  // than a client-side filter — which means the filter belongs in the key.
  const requirementsQuery = useRequirementsV10GroupsRequirementsList(
    groupId,
    {
      ...(statusFilter === 'all' ? {} : { status: statusFilter }),
      pageSize: REQUIREMENTS_PAGE_SIZE,
    },
    {
      query: {
        enabled: Boolean(groupId),
        keepPreviousData: true,
        refetchInterval: pollWhileLive(runStatus),
      },
    }
  );

  const latestRunId = groupQuery.data?.group.latestRun?.id;
  const changesQuery = useBenefitConfigurationV10ChangesList(
    { run: latestRunId, pageSize: 100 },
    {
      query: {
        enabled: latestRunId !== undefined,
        refetchInterval: pollWhileLive(runStatus),
      },
    }
  );

  const startRun = useStartRun({
    groupId,
    employerId: groupQuery.data?.group.employer,
    benefitProgrammes: groupQuery.data?.group.programmes ?? [],
  });

  // Back-link built from the employer on the group, so it also works when
  // someone opens a group link directly (no employer in the current URL).
  const employerId = groupQuery.data?.group.employer;
  const onBack = useCallback(
    () =>
      navigate(
        employerId === undefined
          ? '/requirements'
          : `/requirements?employer=${employerId}`
      ),
    [employerId, navigate]
  );

  // Stable empty array while there is no data yet, to avoid re-triggering
  // downstream memos on every render before the first fetch settles.
  const visible = useMemo(
    () => requirementsQuery.data?.results ?? [],
    [requirementsQuery.data]
  );
  const sections = useMemo(() => groupByDomain(visible), [visible]);

  const openIndex = visible.findIndex((req) => req.id === openRequirementId);
  const openRequirement = openIndex === -1 ? undefined : visible[openIndex];

  // If the requirement in the URL is no longer in the visible list (e.g. its
  // status changed and a poll re-fetched the list), remove the stale ID so the
  // drawer does not open to an empty panel.
  useEffect(
    function clearStaleOpenRequirement() {
      if (
        openRequirementId !== undefined &&
        visible.length > 0 &&
        openIndex === -1
      ) {
        setOpenRequirementId(undefined);
      }
    },
    [openRequirementId, visible, openIndex, setOpenRequirementId]
  );

  const onCloseRequirement = useCallback(
    () => setOpenRequirementId(undefined),
    [setOpenRequirementId]
  );
  const onPrevious = useCallback(() => {
    if (openIndex > 0) setOpenRequirementId(visible[openIndex - 1].id);
  }, [openIndex, visible, setOpenRequirementId]);
  const onNext = useCallback(() => {
    if (openIndex !== -1 && openIndex < visible.length - 1) {
      setOpenRequirementId(visible[openIndex + 1].id);
    }
  }, [openIndex, visible, setOpenRequirementId]);

  const onStartUpdate = useCallback(
    () => void startRun.start('UPDATE'),
    [startRun]
  );
  const onStartBuild = useCallback(
    () => void startRun.start('CREATE'),
    [startRun]
  );

  const children = useMemo(
    () => groupQuery.data?.children ?? [],
    [groupQuery.data]
  );

  const ownStatusCounts =
    groupQuery.data?.group.statusCounts ?? EMPTY_STATUS_COUNTS;
  const statusCounts = groupQuery.data?.group.statusCountsInTree;

  // Memoised so poll-driven re-renders (every 5 s while a run is live) do not
  // recompute these on every tick.
  const chipCounts = useMemo(
    () => (statusCounts ? requirementChipCounts(statusCounts) : {}),
    [statusCounts]
  );

  const totalsByDomain = useMemo(
    () =>
      new Map<string | null, DomainTotals>([
        [null, asDomainTotals(ownStatusCounts)],
        ...children.map((child): [string | null, DomainTotals] => [
          child.domain,
          asDomainTotals(child.statusCounts),
        ]),
      ]),
    [ownStatusCounts, children]
  );

  const awaiting = useMemo(
    () =>
      (changesQuery.data?.results ?? []).filter((change) => change.decidable),
    [changesQuery.data]
  );

  const matching = requirementsQuery.data?.count ?? 0;

  return {
    isLoading: groupQuery.isLoading,
    requirementsLoading: requirementsQuery.isLoading,
    group: groupQuery.data?.group,
    runs: groupQuery.data?.runs ?? [],
    // Requirements list
    visible,
    matching,
    truncated: matching > visible.length,
    sections,
    // Status counts + filter chips
    statusCounts,
    chipCounts,
    // Filter
    statusFilter,
    onFilterChange: setStatusFilter,
    // Side panel
    openRequirement,
    openRequirementId,
    openIndex,
    totalVisible: visible.length,
    onSelectRequirement: setOpenRequirementId,
    onCloseRequirement,
    onPrevious,
    onNext,
    // Domain totals (for section headings)
    totalsByDomain,
    // Changes
    awaiting,
    // Run management
    startRun,
    onStartUpdate,
    onStartBuild,
    // Navigation
    onBack,
  };
};
